/**
 * Excel (.xlsx) export, with no third-party dependency.
 *
 * An .xlsx file is a zip of XML parts. This module writes those parts and packs
 * them with a minimal STORE-only zip writer, which is enough for Excel, LibreOffice
 * and Google Sheets to open the result natively — no "the extension doesn't match
 * the format" warning that a renamed CSV or SpreadsheetML file produces, and no
 * dependency to keep patched.
 *
 * Usage:
 *     exportToExcel({
 *         fileName: 'stock-report',
 *         sheets: [{ name: 'Stock', columns: [...], rows: [...] }],
 *     });
 */

export type ExcelColumn<T = any> = {
    /** Column heading. */
    header: string;
    /** Key on the row object, or a function producing the cell value. */
    key: keyof T | ((row: T) => ExcelCellValue);
    /** Approximate character width of the column. */
    width?: number;
};

export type ExcelCellValue = string | number | boolean | Date | null | undefined;

export type ExcelSheet<T = any> = {
    name: string;
    columns: ExcelColumn<T>[];
    rows: T[];
    /** Extra rows appended after the data, e.g. totals. Written in bold. */
    footer?: ExcelCellValue[][];
    /** Free-text lines written above the header, e.g. the report period. */
    title?: string[];
};

export type ExportOptions = {
    /** Without the .xlsx extension — it is appended. */
    fileName: string;
    sheets: ExcelSheet[];
};

// ---------------------------------------------------------------------------
// XML building
// ---------------------------------------------------------------------------

const escapeXml = (value: string): string =>
    value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;')
        // Control characters are illegal in XML 1.0 and make Excel reject the file.
        // eslint-disable-next-line no-control-regex
        .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '');

/** 0 -> A, 25 -> Z, 26 -> AA */
const columnLetter = (index: number): string => {
    let result = '';
    let n = index;
    while (n >= 0) {
        result = String.fromCharCode((n % 26) + 65) + result;
        n = Math.floor(n / 26) - 1;
    }
    return result;
};

/** Excel sheet names cannot exceed 31 chars or contain : \ / ? * [ ] */
const safeSheetName = (name: string, index: number): string => {
    const cleaned = (name || `Sheet${index + 1}`).replace(/[:\\/?*[\]]/g, ' ').trim();
    return cleaned.slice(0, 31) || `Sheet${index + 1}`;
};

const cellXml = (reference: string, value: ExcelCellValue, bold: boolean): string => {
    const style = bold ? ' s="1"' : '';

    if (value === null || value === undefined || value === '') {
        return `<c r="${reference}"${style}/>`;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
        return `<c r="${reference}"${style}><v>${value}</v></c>`;
    }
    if (typeof value === 'boolean') {
        return `<c r="${reference}" t="b"${style}><v>${value ? 1 : 0}</v></c>`;
    }

    const text = value instanceof Date ? value.toISOString().slice(0, 19).replace('T', ' ') : String(value);
    // Inline strings keep every sheet self-contained — no shared string table.
    return `<c r="${reference}" t="inlineStr"${style}><is><t xml:space="preserve">${escapeXml(text)}</t></is></c>`;
};

const rowXml = (values: ExcelCellValue[], rowNumber: number, bold: boolean): string => {
    const cells = values
        .map((value, index) => cellXml(`${columnLetter(index)}${rowNumber}`, value, bold))
        .join('');
    return `<row r="${rowNumber}">${cells}</row>`;
};

const valueOfCell = (row: any, column: ExcelColumn): ExcelCellValue => {
    if (typeof column.key === 'function') return column.key(row);
    const value = row?.[column.key as string];
    return value as ExcelCellValue;
};

const sheetXml = (sheet: ExcelSheet): string => {
    const lines: string[] = [];
    let rowNumber = 1;

    for (const line of sheet.title || []) {
        lines.push(rowXml([line], rowNumber++, true));
    }
    if ((sheet.title || []).length > 0) {
        rowNumber++; // blank spacer row
    }

    lines.push(rowXml(sheet.columns.map(c => c.header), rowNumber++, true));

    for (const row of sheet.rows) {
        lines.push(rowXml(sheet.columns.map(column => valueOfCell(row, column)), rowNumber++, false));
    }

    if (sheet.footer && sheet.footer.length > 0) {
        rowNumber++; // blank spacer row
        for (const footerRow of sheet.footer) {
            lines.push(rowXml(footerRow, rowNumber++, true));
        }
    }

    const cols = sheet.columns
        .map((column, index) => {
            const width = column.width ?? Math.min(Math.max(column.header.length + 4, 12), 40);
            return `<col min="${index + 1}" max="${index + 1}" width="${width}" customWidth="1"/>`;
        })
        .join('');

    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><cols>${cols}</cols><sheetData>${lines.join('')}</sheetData></worksheet>`;
};

const STYLES_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts><fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills><borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/></cellXfs><cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles></styleSheet>`;

const ROOT_RELS = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`;

// ---------------------------------------------------------------------------
// Minimal zip writer (STORE — no compression)
// ---------------------------------------------------------------------------

const CRC_TABLE = (() => {
    const table = new Uint32Array(256);
    for (let i = 0; i < 256; i++) {
        let c = i;
        for (let k = 0; k < 8; k++) {
            c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
        }
        table[i] = c >>> 0;
    }
    return table;
})();

const crc32 = (bytes: Uint8Array): number => {
    let crc = 0xffffffff;
    for (let i = 0; i < bytes.length; i++) {
        crc = CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
    }
    return (crc ^ 0xffffffff) >>> 0;
};

type ZipEntry = { name: string; data: Uint8Array; crc: number; offset: number };

const encoder = new TextEncoder();

/** DOS time/date. Anything valid works — Excel does not read it. */
const DOS_TIME = 0;
const DOS_DATE = 0x2821; // 2000-01-01

const buildZip = (files: { name: string; content: string }[]): Blob => {
    const entries: ZipEntry[] = [];
    const chunks: Uint8Array[] = [];
    let offset = 0;

    const push = (bytes: Uint8Array) => {
        chunks.push(bytes);
        offset += bytes.length;
    };

    for (const file of files) {
        const nameBytes = encoder.encode(file.name);
        const data = encoder.encode(file.content);
        const crc = crc32(data);

        const header = new Uint8Array(30 + nameBytes.length);
        const view = new DataView(header.buffer);
        view.setUint32(0, 0x04034b50, true);   // local file header signature
        view.setUint16(4, 20, true);           // version needed
        view.setUint16(6, 0x0800, true);       // UTF-8 file names
        view.setUint16(8, 0, true);            // compression: stored
        view.setUint16(10, DOS_TIME, true);
        view.setUint16(12, DOS_DATE, true);
        view.setUint32(14, crc, true);
        view.setUint32(18, data.length, true); // compressed size
        view.setUint32(22, data.length, true); // uncompressed size
        view.setUint16(26, nameBytes.length, true);
        view.setUint16(28, 0, true);           // extra field length
        header.set(nameBytes, 30);

        entries.push({ name: file.name, data, crc, offset });
        push(header);
        push(data);
    }

    const centralStart = offset;
    for (const entry of entries) {
        const nameBytes = encoder.encode(entry.name);
        const record = new Uint8Array(46 + nameBytes.length);
        const view = new DataView(record.buffer);
        view.setUint32(0, 0x02014b50, true);   // central directory signature
        view.setUint16(4, 20, true);           // version made by
        view.setUint16(6, 20, true);           // version needed
        view.setUint16(8, 0x0800, true);       // UTF-8 file names
        view.setUint16(10, 0, true);           // compression: stored
        view.setUint16(12, DOS_TIME, true);
        view.setUint16(14, DOS_DATE, true);
        view.setUint32(16, entry.crc, true);
        view.setUint32(20, entry.data.length, true);
        view.setUint32(24, entry.data.length, true);
        view.setUint16(28, nameBytes.length, true);
        view.setUint16(30, 0, true);           // extra field length
        view.setUint16(32, 0, true);           // comment length
        view.setUint16(34, 0, true);           // disk number start
        view.setUint16(36, 0, true);           // internal attributes
        view.setUint32(38, 0, true);           // external attributes
        view.setUint32(42, entry.offset, true);
        record.set(nameBytes, 46);
        push(record);
    }

    const end = new Uint8Array(22);
    const endView = new DataView(end.buffer);
    endView.setUint32(0, 0x06054b50, true);    // end of central directory
    endView.setUint16(4, 0, true);             // disk number
    endView.setUint16(6, 0, true);             // disk with central directory
    endView.setUint16(8, entries.length, true);
    endView.setUint16(10, entries.length, true);
    endView.setUint32(12, offset - centralStart, true);
    endView.setUint32(16, centralStart, true);
    endView.setUint16(20, 0, true);            // comment length
    push(end);

    // Cast keeps TS happy across lib.dom versions that type BlobPart narrowly.
    return new Blob(chunks as BlobPart[], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Build the workbook and hand it to the browser as a download. */
export const exportToExcel = ({ fileName, sheets }: ExportOptions): void => {
    if (!sheets.length) throw new Error('exportToExcel needs at least one sheet');

    const named = sheets.map((sheet, index) => ({ ...sheet, name: safeSheetName(sheet.name, index) }));

    const contentTypes = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>${named
        .map(
            (_, index) =>
                `<Override PartName="/xl/worksheets/sheet${index + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`
        )
        .join('')}</Types>`;

    const workbook = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets>${named
        .map((sheet, index) => `<sheet name="${escapeXml(sheet.name)}" sheetId="${index + 1}" r:id="rId${index + 1}"/>`)
        .join('')}</sheets></workbook>`;

    const workbookRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${named
        .map(
            (_, index) =>
                `<Relationship Id="rId${index + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${index + 1}.xml"/>`
        )
        .join('')}<Relationship Id="rId${named.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`;

    const files = [
        { name: '[Content_Types].xml', content: contentTypes },
        { name: '_rels/.rels', content: ROOT_RELS },
        { name: 'xl/workbook.xml', content: workbook },
        { name: 'xl/_rels/workbook.xml.rels', content: workbookRels },
        { name: 'xl/styles.xml', content: STYLES_XML },
        ...named.map((sheet, index) => ({
            name: `xl/worksheets/sheet${index + 1}.xml`,
            content: sheetXml(sheet),
        })),
    ];

    downloadBlob(buildZip(files), `${fileName.replace(/\.xlsx$/i, '')}.xlsx`);
};

/** Convenience wrapper for the common "one table, one sheet" case. */
export const exportTableToExcel = <T,>(options: {
    fileName: string;
    sheetName?: string;
    columns: ExcelColumn<T>[];
    rows: T[];
    footer?: ExcelCellValue[][];
    title?: string[];
}): void =>
    exportToExcel({
        fileName: options.fileName,
        sheets: [
            {
                name: options.sheetName || 'Report',
                columns: options.columns as ExcelColumn[],
                rows: options.rows,
                footer: options.footer,
                title: options.title,
            },
        ],
    });

/** Save any blob under `fileName`. Also used for the settings backup download. */
export const downloadBlob = (blob: Blob, fileName: string): void => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Revoking immediately can cancel the download in Safari; a tick is enough.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/** Timestamp suffix so repeated exports do not overwrite each other. */
export const exportStamp = (): string =>
    new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
